// gridSystem.js — Display (lines) & Drawing (dots) toggles fixed, snapping separate
export default class GridSystem {
  constructor(engine, opts = {}) {
    this.engine = engine;

    this.display = {
      show: engine.showGrid ?? true,
      minPixelSpacing: opts.minPixelSpacing ?? 80,
      color: engine.gridColor ?? "#5f7290",
      mode: opts.mode ?? "classic",                 // "classic" | "late-lines"
      majorAfterMinorPx: opts.majorAfterMinorPx ?? 40,
      dotsOnlyAfterPx: opts.dotsOnlyAfterPx ?? 20,  // dots threshold (px) — keep 20 = current behavior
    };

    this.drawing = {
      enabled: true,          // snapping ON/OFF
      show: true,             // dots visibility ON/OFF (independent)
      stepX: opts.stepX ?? 1,
      stepY: opts.stepY ?? 1,
      originX: opts.originX ?? 0,
      originY: opts.originY ?? 0,
      color: opts.drawingColor ?? (this.display?.color || "#5f7290"),
    };
  }

  // --- snapping helpers (unchanged) ---
  snapScalar(v, axis = "x") {
    if (!this.drawing.enabled) return v;
    const step = axis === "y" ? this.drawing.stepY : this.drawing.stepX;
    const origin = axis === "y" ? this.drawing.originY : this.drawing.originX;
    return Math.round((v - origin) / step) * step + origin;
  }
  snapPoint({ x, y }) {
    return { x: this.snapScalar(x, "x"), y: this.snapScalar(y, "y") };
  }

  calculateNiceGridSpacing(minPixelDist, scale) {
    const worldDist = minPixelDist / scale;
    const powerOf10 = 10 ** Math.floor(Math.log10(worldDist));
    const normalizedDist = worldDist / powerOf10;

    let nice;
    if (normalizedDist < 1.5) nice = 1;
    else if (normalizedDist < 3.5) nice = 2;
    else if (normalizedDist < 7.5) nice = 5;
    else nice = 10;

    return nice * powerOf10;
  }

  // --- main draw ---
  drawDisplayGrid() {
    const eng = this.engine;
    const ctx = eng.ctx;
    const width = eng.canvas.width / eng.dpr;
    const height = eng.canvas.height / eng.dpr;

    ctx.save();
    ctx.clearRect(0, 0, width, height);

    // NOTE: pehle yahan "if (!this.display.show) return;" tha — jiski wajah se dots bhi off ho jaate the.
    // Ab transform once, then draw lines/dots independently:
    ctx.translate(eng.offsetX, eng.offsetY);
    ctx.scale(eng.scale, eng.scale);

    const startX = -eng.offsetX / eng.scale;
    const startY = -eng.offsetY / eng.scale;
    const endX = startX + width / eng.scale;
    const endY = startY + height / eng.scale;

    // ---- Minor Grid (dots) zoom rule ----
    const minorStepX = this.drawing.stepX;
    const minorStepY = this.drawing.stepY;
    const minorPixelSpacing = minorStepX * eng.scale; // stepX is canonical for threshold
    const shouldShowDotsAtZoom = minorPixelSpacing > this.display.dotsOnlyAfterPx;

    // ---- Draw DOTS (Drawing Grid) ----
    if (this.drawing.show && shouldShowDotsAtZoom) {
      const firstX = Math.floor(startX / minorStepX) * minorStepX;
      const firstY = Math.floor(startY / minorStepY) * minorStepY;

      const dotR = 1.2 / eng.scale;
      ctx.fillStyle = this.drawing.color;
      ctx.globalAlpha = 0.45;

      for (let x = firstX; x <= endX; x += minorStepX) {
        for (let y = firstY; y <= endY; y += minorStepY) {
          ctx.beginPath();
          ctx.arc(x, y, dotR, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    }

    // ---- Draw LINES (Display Grid) ----
    // Lines sirf tab, jab display.show true ho *aur* dots nahi dikh rahe (last zoom pe dots-only rule)
    if (this.display.show && !shouldShowDotsAtZoom) {
      const major = this.calculateNiceGridSpacing(this.display.minPixelSpacing, eng.scale);
      const firstMajorX = Math.floor(startX / major) * major;
      const firstMajorY = Math.floor(startY / major) * major;

      const showMajor =
        this.display.mode === "classic" ||
        (this.display.mode === "late-lines" && minorPixelSpacing >= this.display.majorAfterMinorPx);

      if (showMajor) {
        ctx.beginPath();
        ctx.lineWidth = 1 / eng.scale;
        ctx.strokeStyle = this.display.color;
        ctx.globalAlpha = 0.55;

        for (let x = firstMajorX; x <= endX; x += major) {
          ctx.moveTo(x, startY); ctx.lineTo(x, endY);
        }
        for (let y = firstMajorY; y <= endY; y += major) {
          ctx.moveTo(startX, y); ctx.lineTo(endX, y);
        }
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    ctx.restore();
  }

  // --- API: setters / getters (names kept same to avoid breaking call sites) ---
  setDisplaySpacing(px) { this.display.minPixelSpacing = Math.max(5, +px || 20); }
  setDisplayColor(c) { this.display.color = c || this.display.color; }
  setDisplayShow(v) { this.display.show = !!v; }

  setDrawingColor(c) { this.drawing.color = c || this.drawing.color; }
  setDrawingShow(v) { this.drawing.show = !!v; }

  setDrawingStep({ stepX, stepY }) {
    if (stepX != null) this.drawing.stepX = Math.max(0.000001, +stepX);
    if (stepY != null) this.drawing.stepY = Math.max(0.000001, +stepY);
  }
  setOrigin({ x, y }) {
    if (x != null) this.drawing.originX = +x;
    if (y != null) this.drawing.originY = +y;
  }
  setEnabled(on) { this.drawing.enabled = !!on; } // snapping only
}
